import { Component, HostListener, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsersService } from '../../../../core/services/user/users.service';
import { Router, RouterModule } from '@angular/router';
import {
  UserListModel,
  UserStatus,
} from '../../../../core/models/user/user-list.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserRole } from '../../../../../enums/roles.enum';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { FilterModel } from '../../../../core/models/filters/filters.model';
import { TablesComponent } from '../../../components/tables/tables.component';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { ToastService } from '../../../../core/services/toast.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    TablesComponent,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  providers: [DatePipe],
})
export class UserListComponent {
  userList: UserListModel[] = [];
  totalUserCount: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  pageHeight: number;
  userListLoading: boolean = true;
  deleteModalId: string = '';
  userStatus: UserStatus[];
  roles: any = [];
  url: string = `${environment.apiLocations.identity}/Users`;
  organizationList: any = [];
  userRole: any = UserRole;

  filters: FilterModel[] = [
    //NameSurname
    {
      type: 'input',
      label: 'nameSurname',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //Role
    {
      type: 'select',
      label: 'role',
      placeholder: 'selectRole',
      filterId: 'RoleId',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'email',
      placeholder: 'filterByEmail',
      filterId: 'Email',
      value: null,
    },
    //Derm No
    {
      type: 'input',
      label: 'dermNo',
      placeholder: 'filterByDermNo',
      filterId: 'PIDNo',
      value: null,
    },
  ];
  sort: SortModel | null = null;
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-150',
    'w-200',
    'w-225',
    'w-125',
    'w-125',
    'w-125',
    'w-125',
    'w-200',
  ];

  constructor(
    private userService: UsersService,
    private translate: TranslateService,
    private router: Router,
    private toastService: ToastService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);

    this.columns = [
      { key: 'nameAndSurname', translateKey: 'user' },
      { key: 'roleName', translateKey: 'role' },
      { key: 'email', translateKey: 'email' },
      { key: 'pid', translateKey: 'dermNo' },
      { key: 'createdDateTime', translateKey: 'created' },
      { key: 'lastLoginDate', translateKey: 'lastLogin' },
      { key: 'userStatus', translateKey: 'status' },
      {
        key: 'actions',
        translateKey: 'actions',
        actionTypes: ['activate', 'block', 'edit', 'delete'],
      },
    ];
    setTimeout(() => {
      this.tableHeader = this.translate.instant('userList');
      this.footerText = this.translate.instant('footerText.users');
    }, 100);
  }

  ngOnInit(): void {
    // this.getUserList(this.currentPage, this.pageSize);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const newHeight = window.innerHeight;

    if (newHeight !== this.pageHeight) {
      this.calculatePageSize();
    }
  }

  private calculatePageSize(): void {
    const pageHeight = window.innerHeight;
    this.pageHeight = pageHeight;

    if (pageHeight < 600) {
      this.pageSize = 5;
    } else if (pageHeight < 1000) {
      this.pageSize = 10;
    } else if (pageHeight < 1400) {
      this.pageSize = 25;
    } else {
      this.pageSize = 50;
    }
    this.getUserList(
      this.currentPage,
      this.pageSize,
      this.filters ? this.filters : null,
      this.sort ? this.sort : null
    );
  }

  filterUserList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getUserList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getUserList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getUserList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getUserList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.userListLoading = true;
    this.userService.getUserList(page, size, filters, sort).subscribe({
      next: (res) => {
        this.userList = res.items.map((user) => ({
          ...user,
          roleName: this.translate.instant(user.roleName),
        }));
        this.totalUserCount = res.count;
        this.totalPage = res.pages;
        this.pageSize = res.size;
        this.currentPage = page;
        this.userListLoading = false;
      },
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getUserList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getUserList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  deleteUser(id: string): void {
    if (id) {
      this.userService.deleteUser(id).subscribe({
        next: () => {
          this.toastService.showToast(
            this.translate.instant('successDeleteUserDesc'),
            'success'
          );
          this.getUserList(1, this.pageSize, this.filters);
        },
      });
    }
  }

  blockUser(id: string): void {
    this.userService.blockUser(id).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successBlockUserDesc'),
          'success'
        );

        this.getUserList(1, this.pageSize, this.filters);
      },
    });
  }

  activateUser(id: string): void {
    this.userService.activateUser(id).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successActiveUserDesc'),
          'success'
        );

        this.getUserList(1, this.pageSize, this.filters);
      },
    });
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'Patient.PidNo';
      case 'nameAndSurname':
        return 'Name';
      case 'patientStatus':
        return 'PatientRecordCases.PatientStatus';
      case 'createdBy':
        return 'CreatedUser';
      case 'roleName':
        return 'Role.Name';
      default:
        return column;
    }
  }

  actionButtonClick(actionType: string, row: any): void {
    switch (actionType) {
      case 'activate':
        this.activateUser(row.id);
        break;
      case 'block':
        this.blockUser(row.id);
        break;
      case 'edit':
        this.router.navigate(['/user/edit', row.id]);
        break;
      case 'delete':
        this.deleteUser(row.id);
        break;
    }
  }
}
